import pluralize from "pluralize";
import { Flex, OverlineText, Stack, Text } from "@hopper-ui/components";
import type { BulkOperationSummary } from "../../api/types.ts";
import {
    GridCardListItem,
    type NumberFormatters,
    Progress,
    uppercaseFirstCharacter,
    useElapsedTime,
    useNumberFormatters
} from "@sg-protect/components";
import { MixpanelProperty, useTrackButtonClicked } from "@sg-protect/shell";
import type { RunningOperation } from "../remediation/BulkProvider.tsx";
import { ACTIVITY_TYPE_ICON, BULK_ACTION_ACTIVITY_TYPE } from "../../utils/actionIcons.ts";
import { type BulkTargetProgress, getBulkTargetProgress } from "../../utils/bulkTargetProgress.ts";
import { ACTION_LABELS } from "../../utils/bulkActionLabels.ts";
import { OperationCardHeader } from "./OperationCardHeader.tsx";
import { useOperationActivityLogHref } from "./useOperationActivityLogHref.ts";

export interface BulkOperationCardProps {
    operation: RunningOperation;
    summary?: BulkOperationSummary;
    tenantName?: string;
}

function getTargetLabel(
    progress: BulkTargetProgress | undefined,
    operation: RunningOperation,
    tenantName: string | undefined,
    formatNumber: NumberFormatters["formatNumber"]
) {
    if (progress && progress.total > 1) {
        return `${formatNumber(progress.total)} ${pluralize(progress.entity, progress.total)}`;
    }

    // The backend has no name for tenant settings, so a tenant-level operation is named after the tenant.
    return operation.targetDisplayName ?? (ACTION_LABELS[operation.action].isSingleton ? tenantName : undefined);
}

export function BulkOperationCard({ operation, summary, tenantName }: BulkOperationCardProps) {
    const trackButtonClicked = useTrackButtonClicked();
    const { formatNumber } = useNumberFormatters();
    const href = useOperationActivityLogHref(operation.bulkId, "running");
    const elapsedTime = useElapsedTime(operation.createdAt);
    const progress = summary ? getBulkTargetProgress(summary) : undefined;
    const labels = ACTION_LABELS[operation.action];
    const Icon = ACTIVITY_TYPE_ICON[BULK_ACTION_ACTIVITY_TYPE[operation.action]];
    const isBulk = progress !== undefined && progress.total > 1;
    const targetLabel = getTargetLabel(progress, operation, tenantName, formatNumber);

    const handleAction = () => {
        trackButtonClicked("Running Operation Card", "Activity Center", {
            // Same values as the backend's BulkOperationProcessed (C# enum names), so the two events join on Action.
            [MixpanelProperty.Action]: uppercaseFirstCharacter(operation.action),
            [MixpanelProperty.BulkOperationId]: operation.bulkId,
            [MixpanelProperty.IsAutomationInitiated]: operation.isPolicy,
            [MixpanelProperty.Status]: "Running"
        });
    };

    return (
        <GridCardListItem
            id={operation.bulkId}
            textValue={operation.isPolicy ? `Policy: ${labels.running}` : labels.running}
            href={href}
            onAction={href ? handleAction : undefined}
            padding="inset-md"
        >
            <Stack gap="stack-sm" width="100%">
                <OperationCardHeader
                    title={labels.running}
                    icon={<Icon />}
                    isLink={href !== undefined}
                    isPolicy={operation.isPolicy}
                />
                {targetLabel && (
                    <Text size="xs" color="neutral-weak" wordBreak="break-word">
                        {targetLabel}
                    </Text>
                )}
                {labels.runningHint && (
                    <Text size="xs" color="neutral-weak">
                        {labels.runningHint}
                    </Text>
                )}
                <Progress
                    isIndeterminate={!isBulk || progress.done === 0}
                    value={progress?.done}
                    max={progress?.total}
                    fillColor="primary-strong"
                    aria-label="Operation progress"
                />
                <Flex justifyContent="space-between" gap="inline-sm">
                    <OverlineText color="neutral-weak">{`Started ${elapsedTime}`}</OverlineText>
                    {isBulk && (
                        <OverlineText color="neutral-weak">
                            {`${formatNumber(progress.done)} of ${formatNumber(progress.total)}`}
                        </OverlineText>
                    )}
                </Flex>
            </Stack>
        </GridCardListItem>
    );
}
